import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { injectParams } from 'ngxtension/inject-params';
import { firstValueFrom } from 'rxjs';

import { IconComponent } from '@shared/components/icon/icon.component';
import { NotesComponent } from '@shared/components/notes/notes.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { Currency, EventType, GqlService, WorkflowStep } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService, injectPermissions } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { Utils } from '@shared/utils/utils';

import { AdjustmentsToPrepaidComponent } from './components/adjustments-to-prepaid/adjustments-to-prepaid.component';
import { injectGetPrepaidSummaryQuery } from './models/get-prepaid-summary-query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, RouterLink, NotesComponent, AdjustmentsToPrepaidComponent],
  selector: 'aux-prepaid-detail',
  templateUrl: './prepaid-detail.component.html',
})
export class PrepaidDetailComponent {
  authService = inject(AuthService);
  changeDetectorRef = inject(ChangeDetectorRef);
  destroyRef = inject(DestroyRef);
  eventQuery = inject(EventQuery);
  gqlService = inject(GqlService);
  launchDarklyService = inject(LaunchDarklyService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  workflowQuery = inject(WorkflowQuery);

  permissions = injectPermissions({
    hasEditTerms: 'PERMISSION_UPDATE_PREPAIDS',
  });
  prepaidSummaryQuery = injectGetPrepaidSummaryQuery();
  vendorId = injectParams('id');

  iCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);
  workflowName = WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_INVOICES;
  isInvoiceFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(this.workflowName);
  isQuarterCloseEnabled = this.workflowQuery.isWorkflowAvailable;

  isEditDisabled = computed(() => {
    return !!(
      this.isInvoiceFinalized() &&
      (this.isQuarterCloseEnabled() || this.iCloseMonthsProcessing())
    );
  });
  isNotesDisabled = computed(() => {
    return this.isEditDisabled() || !this.permissions().hasEditTerms;
  });
  loading = computed(() => this.prepaidSummaryQuery.isLoading());
  notesTooltipText = computed(() => {
    if (this.isEditDisabled()) {
      return this.prepaidLockTooltip();
    }

    if (!this.permissions().hasEditTerms) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return '';
  });
  prepaid = computed(() => {
    const data = this.prepaidSummaryQuery.data();
    const vendorId = this.vendorId();

    if (!data || !vendorId) {
      return null;
    }

    return data.find((prepaid) => prepaid.vendor_id === vendorId);
  });
  vendorCurrency = computed(() => {
    const prepaid = this.prepaid();

    if (prepaid && prepaid.vendor_currency) {
      return prepaid.vendor_currency.replace('CURRENCY_', '') as Currency;
    }

    return Currency.USD;
  });
  prepaidLongTermBalance = computed(() => {
    const prepaid = this.prepaid();

    return Utils.currencyFormatter(prepaid?.long_term_amount || 0, {}, this.vendorCurrency());
  });
  prepaidShortTermBalance = computed(() => {
    const prepaid = this.prepaid();

    return Utils.currencyFormatter(prepaid?.short_term_amount || 0, {}, this.vendorCurrency());
  });
  prepaidTotalBalance = computed(() => {
    const prepaid = this.prepaid();

    return Utils.currencyFormatter(prepaid?.total_prepaid_balance || 0, {}, this.vendorCurrency());
  });
  vendorName = computed(() => {
    const prepaid = this.prepaid();

    return prepaid?.vendor_name || '';
  });

  adjustmentsToPrepaid = viewChild(AdjustmentsToPrepaidComponent);

  notesControl = new FormControl();
  _syncNotes = effect(() => {
    const prepaid = this.prepaid();

    this.notesControl.setValue(prepaid?.terms || '');
  });
  prepaidLockTooltip = this.workflowQuery.prepaidLockTooltip;
  prepaidsLink = `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.PREPAIDS}`;

  cancelNotes() {
    const prepaid = this.prepaid();

    this.notesControl.setValue(prepaid?.terms || '');
    this.notesControl.markAsPristine();
  }

  async canDeactivate(): Promise<boolean> {
    const prepaid = this.prepaid();
    const notes = this.notesControl.value;
    const hasNotesChanges = notes !== (prepaid?.terms || '');
    const hasAdjustmentsChanges = this.adjustmentsToPrepaid()?.hasChanges();

    if (hasNotesChanges || hasAdjustmentsChanges) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  async onAdjustmentsSaved() {
    await this.prepaidSummaryQuery.refetch();
  }

  async saveNotes() {
    const result = await firstValueFrom(
      this.gqlService.updatePrepaidTerms$({
        terms: this.notesControl.value,
        vendor_id: this.vendorId() || '',
      })
    );

    if (result.success) {
      await this.prepaidSummaryQuery.refetch();
      this.overlayService.success(MessagesConstants.SAVED);
    }

    this.notesControl.markAsPristine();
  }
}
